// FR-6 practice report & error notebook + FR-10 PNG export (frontend-only).

import type { Page } from '../page';
import { el, radarChart } from '../components';
import { toast } from '../store';
import { listReports, deleteReport, exportAllData } from '../../storage/db';
import { midiToName } from '../../audio/freq';
import type { PracticeReport } from '../../types';

export function reportPage(): Page {
  return {
    async mount(container) {
      container.append(
        el('h1', { text: '练习报告与错题本' }),
        el('p', { class: 'sub', text: '历史练习记录 · 雷达图 · 错题回看' }),
        el('div', { class: 'row wrap' }, [
          el('button', { class: 'chip', text: '🔄 刷新', onclick: () => rerender() }),
          el('button', { class: 'chip', text: '⬇ 导出全部数据(JSON)', onclick: async () => {
            const data = await exportAllData();
            const blob = new Blob([data], { type: 'application/json' });
            download(blob, 'violinlab-data.json');
            toast('已导出 JSON');
          } }),
        ]),
      );
      const listWrap = el('div');
      container.append(listWrap);

      function rerender() {
        container.querySelectorAll('.page,.card,.empty').forEach(() => undefined);
        render();
      }

      async function render() {
        listWrap.innerHTML = '';
        const reports = await listReports();
        if (reports.length === 0) {
          listWrap.append(el('div', { class: 'card' }, [el('div', { class: 'empty', text: '还没有练习记录，去练一曲吧 🎻' })]));
          return;
        }
        for (const r of reports) listWrap.append(reportRow(r, () => showDetail(r)));
      }

      function showDetail(r: PracticeReport) {
        listWrap.innerHTML = '';
        const radarCanvas = el('canvas');
        radarChart(radarCanvas, ['音准', '节奏', '完成度'], [r.radar_data['音准'] ?? 0, r.radar_data['节奏'] ?? 0, r.radar_data['完成度'] ?? 0]);
        const errItems = r.error_records.map((e) =>
          el('div', { class: 'row between tiny', style: 'padding:6px 0;border-bottom:1px solid var(--border)' }, [
            el('span', { text: `第 ${e.measure ?? '?'} 小节 · ${labelError(e.type)}` }),
            el('span', { class: 'muted', text: e.expectedPitch != null ? `${midiToName(e.expectedPitch)} → ${e.actualPitch != null ? midiToName(e.actualPitch) : '?'}` : '' }),
          ]),
        );
        const back = el('button', { class: 'chip', text: '← 返回列表' });
        back.onclick = () => render();
        const exp = el('button', { class: 'primary', text: '🖼 导出长图(PNG)' });
        exp.onclick = () => exportPNG(r);
        const practice = el('button', { class: 'chip', text: '🎯 去乐谱页重练' });
        practice.onclick = () => { location.hash = '#/score'; };
        listWrap.append(el('div', { class: 'card' }, [
          el('div', { class: 'row between' }, [el('h2', { text: r.score_id }), back]),
          el('div', { class: 'tiny muted', text: `${new Date(r.started_at).toLocaleString()} · 总分 ${r.overall_score}` }),
          el('div', { class: 'row center', style: 'gap:20px;margin:12px 0' }, [radarCanvas, el('div', { class: 'col tiny' }, [
            el('span', { text: `音准: ${r.pitch_score}` }),
            el('span', { text: `节奏: ${r.rhythm_score}` }),
            el('span', { text: `错题: ${r.error_records.length}` }),
          ])]),
          el('h2', { text: `错题 (${r.error_records.length})` }),
          ...errItems,
          el('div', { class: 'row wrap', style: 'margin-top:14px' }, [exp, practice]),
        ]));
      }

      render();
    },
  };
}

function reportRow(r: PracticeReport, onOpen: () => void): HTMLElement {
  const card = el('div', { class: 'card', style: 'cursor:pointer' }, [
    el('div', { class: 'row between' }, [
      el('div', {}, [el('div', { text: r.score_id, style: 'font-weight:600' }), el('div', { class: 'tiny muted', text: new Date(r.started_at).toLocaleString() })]),
      el('div', { class: 'col', style: 'align-items:flex-end' }, [
        el('span', { text: String(r.overall_score), style: 'font-size:22px;font-weight:700', class: r.overall_score >= 80 ? 'tag-ok' : r.overall_score >= 60 ? 'tag-warn' : 'tag-bad' }),
        el('span', { class: 'tiny muted', text: `错题 ${r.error_records.length}` }),
      ]),
    ]),
  ]);
  card.onclick = onOpen;
  const del = el('button', { class: 'chip tiny', text: '删除', style: 'margin-top:8px' });
  del.onclick = async (ev) => { ev.stopPropagation(); await deleteReport(r.id); toast('已删除'); card.remove(); };
  card.append(del);
  return card;
}

function labelError(t: string): string {
  return { noteMistake: '错音', timingMistake: '错拍', tuningFail: '音准未过', timbreFail: '音色未过' }[t] ?? t;
}

function exportPNG(r: PracticeReport): void {
  const W = 1080, H = 1400;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#14111c'; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#ece7f5'; ctx.font = 'bold 44px sans-serif'; ctx.textAlign = 'left';
  ctx.fillText('练习报告', 60, 90);
  ctx.fillStyle = '#a79fc4'; ctx.font = '24px sans-serif';
  ctx.fillText(`${r.score_id} · ${new Date(r.started_at).toLocaleString()}`, 60, 130);
  // Score big
  ctx.fillStyle = r.overall_score >= 80 ? '#3ddc84' : r.overall_score >= 60 ? '#ffb454' : '#ff5470';
  ctx.font = 'bold 120px sans-serif'; ctx.textAlign = 'center';
  ctx.fillText(String(r.overall_score), W / 2, 300);
  ctx.fillStyle = '#a79fc4'; ctx.font = '28px sans-serif';
  ctx.fillText('总分', W / 2, 340);
  // Radar
  const radarCanvas = document.createElement('canvas');
  radarChart(radarCanvas, ['音准', '节奏', '完成度'], [r.radar_data['音准'] ?? 0, r.radar_data['节奏'] ?? 0, r.radar_data['完成度'] ?? 0], 360);
  ctx.drawImage(radarCanvas, (W - 360) / 2, 380);
  // Stats
  ctx.fillStyle = '#ece7f5'; ctx.font = '28px sans-serif'; ctx.textAlign = 'left';
  ctx.fillText(`音准 ${r.pitch_score}    节奏 ${r.rhythm_score}    错题 ${r.error_records.length}`, 60, 820);
  ctx.fillText('错题明细', 60, 900);
  ctx.fillStyle = '#a79fc4'; ctx.font = '24px sans-serif';
  let y = 950;
  for (const e of r.error_records.slice(0, 12)) {
    const line = `第 ${e.measure ?? '?'} 小节 · ${labelError(e.type)}` + (e.expectedPitch != null ? `  (${midiToName(e.expectedPitch)} → ${e.actualPitch != null ? midiToName(e.actualPitch) : '?'})` : '');
    ctx.fillText(line, 60, y);
    y += 40;
  }
  ctx.fillStyle = '#6f6889'; ctx.font = '20px sans-serif';
  ctx.fillText('ViolinLab · 小提琴学习助手', 60, H - 40);
  canvas.toBlob((blob) => { if (blob) { download(blob, `report-${r.id}.png`); toast('已导出长图'); } }, 'image/png');
}

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
